import { Injectable, signal, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CartService } from '../../shop/services/cart.service';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, catchError, throwError, EMPTY } from 'rxjs';
import { Router } from '@angular/router';
import {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  User,
  AuthState,
  PhoneRegistrationStep1,
  PhoneRegistrationStep2,
  PhoneRegistrationStep3,
  PhoneRegistrationStep4,
  VerifyEmailRequest,
  VerifyPhoneRequest,
  VerifyPhoneCodeRequest,
  ResendCodeRequest,
  PasswordResetInitRequest,
  PasswordResetVerifyRequest,
  PasswordResetCompleteRequest,
  RefreshTokenRequest,
  RefreshTokenResponse,
  VerifyTokenRequest,
  SocialLoginRequest,
} from '../models/auth.model';
import { environment } from '../../../../environments/environment';
import { StorageSecurityService } from '../../../core/services/storage-security.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly baseUrl = environment.apiUrl;
  private readonly TOKEN_KEY = 'access_token';
  private readonly REFRESH_KEY = 'refresh_token';
  private readonly USER_KEY = 'user_data';
  private readonly SERIAL_KEY = 'device_serial_number';

  // New properties for social login
  // Keys are now in environment files
  private googleClientId = environment.googleClientId;
  private facebookAppId = environment.facebookAppId;
  private appleClientId = environment.appleClientId;
  private appleRedirectURI = environment.appleRedirectURI;

  // Auth state as signal
  authState = signal<AuthState>({
    isAuthenticated: false,
    user: null,
    accessToken: null,
    refreshToken: null
  });

  constructor(
    private http: HttpClient,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private storageSecurity: StorageSecurityService
  ) {
    // Load auth state from localStorage on init
    this.loadAuthState();
  }

  /**
   * Load authentication state from localStorage
   */
  private loadAuthState(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Attempt to get decryptable values
    const rawToken = localStorage.getItem(this.TOKEN_KEY);
    const rawRefresh = localStorage.getItem(this.REFRESH_KEY);
    const rawUser = localStorage.getItem(this.USER_KEY);

    const accessToken = rawToken ? this.storageSecurity.decrypt(rawToken) : null;
    const refreshToken = rawRefresh ? this.storageSecurity.decrypt(rawRefresh) : null;
    const userData = rawUser ? this.storageSecurity.decrypt(rawUser) : null;

    if (accessToken && userData) {
      try {
        // Double check if data is valid JSON or plain text (legacy migration)
        // If decrypt returns "value" that starts with "{" it's likely JSON.
        // If it starts with random characters, maybe it wasn't encrypted?
        // Actually the decrypt function returns original if it fails, so we are safe.

        const user: User = JSON.parse(userData);
        // Only mark as authenticated if NOT an anonymous user
        const isAuthenticated = !user.is_anonymous;
        this.authState.set({
          isAuthenticated,
          user,
          accessToken,
          refreshToken
        });
      } catch (error) {
        console.error('Error parsing user data:', error);
        this.clearAuthState();
      }
    }
  }

  /**
   * Login user
   */
  login(credentials: LoginRequest, enrichUser?: Partial<User>): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.baseUrl}/user-api/auth/login/`, credentials)
      .pipe(
        tap((response: LoginResponse) => {
          // Ensure user is not marked as anonymous
          const authenticatedUser = {
            ...response.user,
            ...enrichUser,
            is_anonymous: false
          };

          // Store tokens and user data
          if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem(this.TOKEN_KEY, this.storageSecurity.encrypt(response.access_token));
            localStorage.setItem(this.REFRESH_KEY, this.storageSecurity.encrypt(response.refresh_token));
            localStorage.setItem(this.USER_KEY, this.storageSecurity.encrypt(JSON.stringify(authenticatedUser)));
          }

          // Update auth state
          this.authState.set({
            isAuthenticated: true,
            user: authenticatedUser,
            accessToken: response.access_token,
            refreshToken: response.refresh_token
          });
        })
      );
  }

  /**
   * Register new user
   */
  register(data: RegisterRequest): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>(`${this.baseUrl}/user-api/register/`, data);
  }

  /**
   * Get or generate device serial number
   */
  private getDeviceSerialNumber(): string {
    if (!isPlatformBrowser(this.platformId)) {
      // Use a fixed valid UUID for SSR to satisfy backend validation while reusing the user
      return '00000000-0000-0000-0000-000000000000';
    }

    let serialNumber = localStorage.getItem(this.SERIAL_KEY);

    if (!serialNumber) {
      // Generate a unique serial number (UUID-like)
      serialNumber = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });

      localStorage.setItem(this.SERIAL_KEY, serialNumber);
    }

    return serialNumber;
  }

  /**
   * Anonymous login for unauthenticated users
   */
  anonymousLogin(): Observable<LoginResponse> {
    const serialNumber = this.getDeviceSerialNumber();

    return this.http.post<LoginResponse>(`${this.baseUrl}/user-api/anonymous-login/`, {
      serial_number: serialNumber
    })
      .pipe(
        tap((response: LoginResponse) => {
          // Mark user as anonymous if not already marked by backend
          const anonymousUser = {
            ...response.user,
            is_anonymous: response.user.is_anonymous ?? true
          };

          // Store tokens and user data
          if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem(this.TOKEN_KEY, this.storageSecurity.encrypt(response.access_token));
            localStorage.setItem(this.REFRESH_KEY, this.storageSecurity.encrypt(response.refresh_token));
            localStorage.setItem(this.USER_KEY, this.storageSecurity.encrypt(JSON.stringify(anonymousUser)));
          }

          // Update auth state (but mark as not fully authenticated)
          this.authState.set({
            isAuthenticated: false, // Anonymous users are not "authenticated"
            user: anonymousUser,
            accessToken: response.access_token,
            refreshToken: response.refresh_token
          });
        })
      );
  }

  /**
   * Logout user
   */
  logout(): void {
    this.clearAuthState();
    this.router.navigate(['/login']);
  }

  private cartService = inject(CartService);

  /**
   * Clear authentication state
   */
  private clearAuthState(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_KEY);
      localStorage.removeItem(this.USER_KEY);
    }

    // Clear cart on logout so a new user doesn't see stale items/prices
    this.cartService.clearCart();

    this.authState.set({
      isAuthenticated: false,
      user: null,
      accessToken: null,
      refreshToken: null
    });
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    const raw = localStorage.getItem(this.TOKEN_KEY);
    return raw ? this.storageSecurity.decrypt(raw) : null;
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    const raw = localStorage.getItem(this.REFRESH_KEY);
    return raw ? this.storageSecurity.decrypt(raw) : null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.authState().isAuthenticated;
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return this.authState().user;
  }

  /**
   * Show error toast
   */
  showErrorToast(message: string): void {
    // You can implement a toast service or use a simple alert for now
    // For now, we'll emit an event or use a simple approach
    console.error('Auth Error:', message);
    if (isPlatformBrowser(this.platformId)) {
      alert(message);
    }
  }

  /**
   * Redirect to home
   */
  redirectToHome(): void {
    this.router.navigate(['/home']);
  }

  // --- Phone Registration ---

  registerPhoneStep1(data: PhoneRegistrationStep1): Observable<any> {
    return this.http.post(`${this.baseUrl}/user-api/phone-registration/`, data);
  }

  registerPhoneStep2(data: PhoneRegistrationStep2): Observable<any> {
    return this.http.post(`${this.baseUrl}/user-api/phone-registration/verify/`, data);
  }

  registerPhoneStep3(data: PhoneRegistrationStep3): Observable<any> {
    return this.http.post(`${this.baseUrl}/user-api/phone-registration/details/`, data);
  }

  registerPhoneStep4(data: PhoneRegistrationStep4): Observable<any> {
    return this.http.post(`${this.baseUrl}/user-api/phone-registration/complete/`, data);
  }


  // --- Verification ---

  verifyEmail(data: VerifyEmailRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/user-api/verify-registration/verify/`, data);
  }

  verifyPhone(data: VerifyPhoneRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/user-api/verify-phone/`, data);
  }

  verifyPhoneCode(data: VerifyPhoneCodeRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/user-api/verify-phone-code/`, data);
  }

  resendCode(data: ResendCodeRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/user-api/resend-code/`, data);
  }

  // --- Social Auth ---

  // --- Social Login Logic ---

  loginWithGoogle(): Observable<LoginResponse> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;

    return new Observable<LoginResponse>(observer => {
      this.loadGoogleScript().then(() => {
        // @ts-ignore
        const client = google.accounts.oauth2.initTokenClient({
          client_id: this.googleClientId,
          scope: 'email profile openid',
          callback: (response: any) => {
            if (response.access_token) {
              this.socialLoginGoogle({ access_token: response.access_token }).subscribe({
                next: (res) => {
                  observer.next(res);
                  observer.complete();
                },
                error: (err) => observer.error(err)
              });
            } else {
              observer.error('Google login failed or cancelled');
            }
          },
        });
        client.requestAccessToken();
      }).catch(err => observer.error(err));
    });
  }

  loginWithFacebook(): Observable<LoginResponse> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;

    return new Observable<LoginResponse>(observer => {
      this.loadFacebookScript().then(() => {
        // @ts-ignore
        FB.login((response: any) => {
          if (response.authResponse?.accessToken) {
            this.socialLoginFacebook({ access_token: response.authResponse.accessToken }).subscribe({
              next: (res) => {
                observer.next(res);
                observer.complete();
              },
              error: (err) => observer.error(err)
            });
          } else {
            observer.error('Facebook login failed or cancelled');
          }
        }, { scope: 'public_profile,email' });
      }).catch(err => observer.error(err));
    });
  }

  loginWithApple(): Observable<LoginResponse> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;

    return new Observable<LoginResponse>(observer => {
      this.loadAppleScript().then(() => {
        // @ts-ignore
        AppleID.auth.init({
          clientId: this.appleClientId,
          scope: 'name email',
          redirectURI: this.appleRedirectURI,
          usePopup: true
        });

        // @ts-ignore
        AppleID.auth.signIn().then((response: any) => {
          if (response.authorization?.id_token) {
            this.socialLoginApple({ id_token: response.authorization.id_token }).subscribe({
              next: (res) => {
                observer.next(res);
                observer.complete();
              },
              error: (err) => observer.error(err)
            });
          } else {
            observer.error('Apple login failed');
          }
        }).catch((err: any) => observer.error(err));
      }).catch(err => observer.error(err));
    });
  }

  // --- Script Loaders ---

  // --- Script Loaders ---

  private loadGoogleScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).google?.accounts) {
        console.log('Google script already loaded');
        resolve();
        return;
      }
      console.log('Loading Google script...');
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('Google script loaded successfully');
        resolve();
      };
      script.onerror = (err: any) => {
        console.error('Error loading Google script:', err);
        reject('Failed to load Google Sign-In script');
      };
      document.body.appendChild(script);
    });
  }

  private loadFacebookScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).FB) {
        console.log('Facebook script already loaded');
        resolve();
        return;
      }
      console.log('Loading Facebook script...');
      const script = document.createElement('script');
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('Facebook script loaded, initializing...');
        try {
          // @ts-ignore
          FB.init({
            appId: this.facebookAppId,
            cookie: true,
            xfbml: true,
            version: 'v20.0'
          });
          console.log('Facebook initialized');
          resolve();
        } catch (e) {
          console.error('Error initializing Facebook SDK:', e);
          reject('Facebook SDK init failed');
        }
      };
      script.onerror = (err: any) => {
        console.error('Error loading Facebook script:', err);
        reject('Failed to load Facebook SDK');
      };
      document.body.appendChild(script);
    });
  }

  private loadAppleScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).AppleID) {
        console.log('Apple script already loaded');
        resolve();
        return;
      }
      console.log('Loading Apple script...');
      const script = document.createElement('script');
      script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('Apple script loaded successfully');
        resolve();
      };
      script.onerror = (err: any) => {
        console.error('Error loading Apple script:', err);
        reject('Failed to load Apple Sign-In script');
      };
      document.body.appendChild(script);
    });
  }

  // --- Social Auth Endpoints ---

  socialLoginGoogle(data: SocialLoginRequest): Observable<LoginResponse> {
    return this.handleSocialLogin(`${this.baseUrl}/user-api/auth/google`, data);
  }

  socialLoginApple(data: SocialLoginRequest): Observable<LoginResponse> {
    return this.handleSocialLogin(`${this.baseUrl}/user-api/auth/apple`, data);
  }

  socialLoginFacebook(data: SocialLoginRequest): Observable<LoginResponse> {
    return this.handleSocialLogin(`${this.baseUrl}/user-api/auth/facebook`, data);
  }

  private handleSocialLogin(url: string, data: SocialLoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(url, data).pipe(
      tap((response: LoginResponse) => this.handleLoginSuccess(response))
    );
  }

  connectSocial(provider: 'google' | 'apple' | 'facebook', data: SocialLoginRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/user-api/auth/${provider}/connect`, data);
  }

  // --- Password Reset ---

  passwordResetInit(data: PasswordResetInitRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/user-api/custom/password-reset/`, data);
  }

  passwordResetVerify(data: PasswordResetVerifyRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/user-api/custom/password-reset/verify/`, data);
  }

  passwordResetComplete(data: PasswordResetCompleteRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/user-api/custom/password-reset/complete/`, data);
  }

  passwordResetResend(): Observable<any> {
    return this.http.post(`${this.baseUrl}/user-api/custom/password-reset/resend-code/`, {});
  }

  // --- Token Management ---

  refreshToken(refresh: string): Observable<RefreshTokenResponse> {
    return this.http.post<RefreshTokenResponse>(`${this.baseUrl}/user-api/auth/token/refresh/`, { refresh });
  }

  verifyToken(token: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/user-api/auth/token/verify/`, { token });
  }

  // Helper to handle login success
  private handleLoginSuccess(response: any): void {
    console.log('AuthService: handleLoginSuccess called', response); // DEBUG
    
    // dj-rest-auth social login might return `access` instead of `access_token`
    const accessToken = response.access_token || response.access;
    const refreshToken = response.refresh_token || response.refresh;
    
    // Ensure user is not marked as anonymous
    const authenticatedUser = {
      ...response.user,
      is_anonymous: false
    };

    // Store tokens and user data
    if (isPlatformBrowser(this.platformId)) {
      try {
        if (accessToken) localStorage.setItem(this.TOKEN_KEY, this.storageSecurity.encrypt(accessToken));
        if (refreshToken) localStorage.setItem(this.REFRESH_KEY, this.storageSecurity.encrypt(refreshToken));
        localStorage.setItem(this.USER_KEY, this.storageSecurity.encrypt(JSON.stringify(authenticatedUser)));
        console.log('AuthService: Tokens stored in localStorage'); // DEBUG
      } catch (e) {
        console.error('AuthService: Error storing tokens', e);
      }
    }

    // Update auth state
    this.authState.set({
      isAuthenticated: true,
      user: authenticatedUser as User,
      accessToken: accessToken,
      refreshToken: refreshToken
    });
    console.log('AuthService: AuthState signal updated', this.authState()); // DEBUG
  }

  updateToken(accessToken: string): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.TOKEN_KEY, this.storageSecurity.encrypt(accessToken));
    }

    this.authState.update(current => ({
      ...current,
      accessToken
    }));
  }
}
